/** @jsx jsx */
import { jsx, Spinner, Text } from "theme-ui";
import PropTypes from "prop-types";
import PeopleSelector from "gatsby-plugin-hfn-profile/components/PeopleSelector";
import { useCallback, useEffect, useState } from "react";
import {
  DefaultButton,
  Icon,
  MessageBar,
  MessageBarType,
  Stack,
  StackItem,
} from "office-ui-fabric-react";
import { useFirestore } from "react-redux-firebase";
import { formatSys2OdooDate } from "@heartfulnessinstitute/react-hfn-forms/dist/formatting";
import useAuth from "gatsby-plugin-hfn-profile/auth/useAuth";
import TimedAlert from "sites-common/components/ui/TimedAlert";
import {
  DynamicField,
  validateForm,
} from "@heartfulnessinstitute/react-hfn-forms/dist/DynamicForm";
import config, { colors } from "./config";
import EditingRecord from "./EditingRecord";
import {
  aimsMandatoryFlds,
  visitFlds,
  aimsFlds,
  formValidator,
  fieldOverrides,
} from "./fields";

const today = formatSys2OdooDate(new Date());

const { collection } = config;

const NewEntry = ({ adminConfig, onDone }) => {
  const oppy = adminConfig?.id;
  const [people, setPeople] = useState({});
  const [errors, setErrors] = useState({});
  const countPeople = Object.keys(people).length;
  const isError = Object.values(errors).filter((r) => r).length > 0;
  const [success, setSuccess] = useState({});

  const firestore = useFirestore();
  const { srcmProfile = {} } = useAuth();
  const { userFirebaseUid } = srcmProfile;
  const [loader, setLoader] = useState(0);

  const onSuccess = useCallback(
    (id, name) => {
      setPeople((prev) => {
        const x = { ...prev };
        delete x[id];
        return x;
      });
      setSuccess((prev) => ({ ...prev, [id]: name }));
    },
    [setPeople, setSuccess]
  );

  const onFailure = useCallback(
    (id, error) => {
      setErrors((prev) => ({ ...prev, [id]: error }));
    },
    [setErrors]
  );

  const onNewAdd = useCallback(
    (people1) => {
      Object.entries(people1).forEach(([id, person]) => {
        setLoader((x) => x + 1);
        firestore
          .add(collection, {
            ...person,
            uid_added: userFirebaseUid,
            date_added: today,
            oppy,
          })
          .then((/* docRef */) => {
            setLoader((x) => x - 1);
            onSuccess(id, person.name);
          })
          .catch((e) => {
            setLoader((x) => x - 1);
            onFailure(id, e.toString());
            //          dispatch(showAlert({ message: e.toString() }));
          });
      });
    },
    [userFirebaseUid, firestore, oppy, onSuccess, onFailure]
  );

  const onClickValidate = useCallback(() => {
    const e = Object.fromEntries(
      Object.entries(people).map(([id, vals]) => {
        const errors1 = validateForm(
          [...aimsFlds(adminConfig), ...visitFlds(adminConfig)],
          formValidator(adminConfig),
          {},
          vals
        );
        return [id, errors1];
      })
    );
    setErrors(e);
    const isError1 = Object.values(e).filter((r) => r).length > 0;
    if (!isError1) {
      onNewAdd(people);
    }
  }, [people, setErrors, onNewAdd, adminConfig]);

  const addPersons = useCallback(
    (pdict) => {
      const pdictnew = Object.fromEntries(
        Object.entries(pdict).map(([k, v]) => {
          const newPerson = {};
          [...adminConfig.aims_flds, ...aimsMandatoryFlds].forEach((fname) => {
            if (fname in fieldOverrides) {
              const F = fieldOverrides[fname];
              newPerson[fname] = F(v);
            } else if (fname in v) {
              newPerson[fname] = v[fname];
            }
          });
          return [k, newPerson];
        })
      );

      setPeople((prev) => ({ ...prev, ...pdictnew }));
    },
    [setPeople, adminConfig]
  );
  const [secret, setSecret] = useState("");

  useEffect(() => {
    if (Object.keys(success).length > 0 && countPeople === 0 && onDone) {
      onDone();
    }
  }, [countPeople, success, onDone]);

  const secretVerified =
    adminConfig?.use_secret && !!adminConfig?.secrets
      ? adminConfig?.secrets.includes(secret)
      : true;

  return (
    <div sx={{ m: 1 }}>
      {Object.values(success).map((n) => (
        <TimedAlert displayIf time={30000} variant="notify">
          <Text>{n} added!</Text>
        </TimedAlert>
      ))}
      <Text variant="subtitle">Add Volunteers</Text>

      <div sx={{ p: 2, bg: colors.shade6 }}>
        {countPeople > 0 && (
          <div sx={{ p: 3, my: 2 }}>
            <div sx={{ mb: 2 }}>
              {Object.entries(people).map(([id], idx) => {
                return (
                  <div sx={{ p: 1 }}>
                    {idx + 1}
                    <EditingRecord
                      id={id}
                      people={people}
                      setPeople={setPeople}
                      errors={errors}
                      setErrors={setErrors}
                      adminConfig={adminConfig}
                    />
                  </div>
                );
              })}

              <div sx={{ mx: 2 }}>
                <PeopleSelector
                  btnType="default"
                  btnProps={{
                    text: "Select More People",
                    iconProps: { iconName: "Add" },
                  }}
                  onSelectionDone={addPersons}
                  showMyself={false}
                />
              </div>
              <Stack tokens={{ padding: 8, childrenGap: 4 }}>
                {isError ? (
                  <MessageBar messageBarType={MessageBarType.error}>
                    Please fix above errors to proceed
                  </MessageBar>
                ) : (
                  <Stack
                    horizontal
                    tokens={{ childrenGap: 8, padding: 8 }}
                    verticalAlign="center"
                  >
                    <Icon iconName="Accept" />{" "}
                    <Text variant="description">
                      By continuing, I agree that I have taken consent from the
                      above volunteers to view their details and registering
                      them here.
                    </Text>
                  </Stack>
                )}
                <DefaultButton
                  primary
                  disabled={isError || loader}
                  onClick={onClickValidate}
                >
                  Add {countPeople} persons to volunteer list{" "}
                  {!!loader && <Spinner />}
                </DefaultButton>
              </Stack>
            </div>
          </div>
        )}

        {!countPeople && (
          <Stack horizontalAlign="center" tokens={{ padding: 30 }}>
            {adminConfig?.use_secret && (
              <div sx={{ mb: 2 }}>
                <div sx={{ fontSize: "0.8em" }}>
                  This volunteering opportunity is invite-only. Enter referral
                  code received from the organizers to proceed!
                </div>
                <DynamicField
                  name="secret"
                  props={{ placeholder: "Referral Code" }}
                  value={secret}
                  onChange={setSecret}
                />
              </div>
            )}

            <StackItem>
              <PeopleSelector
                btnDisabled={!secretVerified}
                onSelectionDone={addPersons}
                btnProps={{
                  text: "Select Others",
                  iconProps: { iconName: "Family" },
                }}
              />
            </StackItem>
          </Stack>
        )}
      </div>
    </div>
  );
};

NewEntry.propTypes = {
  adminConfig: PropTypes.shape({
    id: PropTypes.string,
    active: PropTypes.bool,
    use_secret: PropTypes.bool,
    secrets: PropTypes.arrayOf(PropTypes.string),
    aims_flds: PropTypes.arrayOf(PropTypes.shape({})),
    visit_flds: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onDone: PropTypes.func,
};
NewEntry.defaultProps = {
  onDone: null,
};
export default NewEntry;
